import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Wraps a subtree so a single bad message / media payload / null prop
 * doesn't blow up the whole page (React 18 unhandled errors unmount the
 * whole tree by default). The fallback shows a tiny card with a retry
 * button that remounts the children.
 *
 * Usage:
 *   <ErrorBoundary label="messages list">
 *     <MessagesList ... />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console + our backend if the caller plugged a
    // reporter. Never throw again here.
     
    console.error(`[ErrorBoundary${this.props.label ? " · " + this.props.label : ""}]`, error, info?.componentStack);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-[12px] text-red-800" data-testid="error-boundary-fallback">
          <div className="flex items-center gap-2 mb-1.5 font-extrabold">
            <AlertTriangle size={14} /> Something broke while rendering {this.props.label || "this section"}
          </div>
          <div className="text-[11px] text-red-700/80 mb-2">{String(this.state.error?.message || this.state.error)}</div>
          <button
            onClick={this.reset}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-white border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-100"
            data-testid="error-boundary-retry-btn"
          >
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
